import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuth, RedirectToSignIn } from '@clerk/react';
import { ThemeProvider } from '@mui/material';

import { getApiAccessToken } from '@client/api';
import { ToastBar } from '@client/toolkit';

import { OnboardingRoute } from '../pods/portfolio/onboarding';
import { AppLoading } from './app-loading';
import theme from './theme';

/**
 * Standalone app shell for portfolio onboarding.
 * Bypasses normal app init (session, tenants, ACL) since the user
 * has no tenant yet — only Clerk JWT authentication is required.
 */
export function OnboardingApp() {
  const { isLoaded, isSignedIn } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);

  // Wait for ClerkTokenBridge to populate the access token
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    const check = () => {
      if (getApiAccessToken()) {
        setTokenReady(true);
        return true;
      }
      return false;
    };

    if (check()) return;

    // Poll briefly until token is available
    const interval = setInterval(() => {
      if (check()) clearInterval(interval);
    }, 100);

    return () => clearInterval(interval);
  }, [isLoaded, isSignedIn]);

  if (!isLoaded || (isSignedIn && !tokenReady)) {
    return <AppLoading show />;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return (
    <ThemeProvider theme={theme}>
      {/* Toaster must live in the onboarding shell too — /onboarding/portfolio
          bypasses the main <App /> which hosts the primary Toaster, so any
          toast.error() fired from onboarding screens would otherwise queue
          invisibly. */}
      <Toaster position="bottom-right">{(t) => <ToastBar toast={t} />}</Toaster>
      <OnboardingRoute />
    </ThemeProvider>
  );
}
